import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Link as LinkIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { adminApi, Notification, NotificationType, NotificationStatus } from '../services/api';
import { NOTIFICATION_TYPE_LABELS } from '../services/labels';

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as NotificationType,
    link: '',
    linkText: '',
    isModal: false,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16), // Default 7 days from now
    status: 'active' as NotificationStatus,
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listNotifications();
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedNotification(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      link: '',
      linkText: '',
      isModal: false,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
      status: 'active',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (notification: Notification) => {
    setSelectedNotification(notification);
    setFormData({
      title: notification.title || '',
      message: notification.message,
      type: notification.type,
      link: notification.link || '',
      linkText: notification.linkText || '',
      isModal: notification.isModal,
      expiresAt: new Date(notification.expiresAt).toISOString().slice(0, 16),
      status: notification.status,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenDelete = (notification: Notification) => {
    setSelectedNotification(notification);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async () => {
    if (!formData.message || !formData.expiresAt) {
      setFormError('Message and Expiration Date are required');
      return;
    }

    try {
      setFormLoading(true);
      setFormError(null);

      // Convert datetime-local to ISO string
      const expiresAtISO = new Date(formData.expiresAt).toISOString();

      if (selectedNotification) {
        // UPDATE: Include status
        await adminApi.updateNotification(selectedNotification.id, {
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
          status: formData.status,
        });
        showSnackbar('Notification updated successfully', 'success');
      } else {
        // CREATE: Don't include status (always starts as 'active')
        await adminApi.createNotification({
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
        });
        showSnackbar('Notification created successfully', 'success');
      }

      setDialogOpen(false);
      fetchNotifications();
    } catch (err: any) {
      setFormError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNotification) return;

    try {
      setFormLoading(true);
      await adminApi.deleteNotification(selectedNotification.id);
      showSnackbar('Notification deleted successfully', 'success');
      setDeleteDialogOpen(false);
      fetchNotifications();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || err.message || 'Delete failed', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'info': return 'primary';
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'feature': return 'secondary';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip 
            label={NOTIFICATION_TYPE_LABELS[params.value as NotificationType] ?? params.value} 
            size="small" 
            color={getTypeColor(params.value as NotificationType)} 
            variant="outlined" 
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip 
            label={params.value} 
            size="small" 
            color={params.value === 'active' ? 'success' : 'default'} 
          />
        </Box>
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 180,
      valueGetter: (value) => new Date(value).toLocaleString(),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.formattedValue}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleOpenDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Notifications
          </Typography>
          <Chip label={`${notifications.length} total`} color="primary" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Notification
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={notifications}
          columns={columns}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
          }}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !formLoading && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedNotification ? 'Edit Notification' : 'Create New Notification'}
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Title (Optional)"
                placeholder="e.g. New Feature Alert!"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Message"
                placeholder="The content of the notification shown to users..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as NotificationType })}
                >
                  <MenuItem value="info">{NOTIFICATION_TYPE_LABELS.info}<Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>(Blue)</Box></MenuItem>
                  <MenuItem value="success">{NOTIFICATION_TYPE_LABELS.success}<Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>(Green)</Box></MenuItem>
                  <MenuItem value="warning">{NOTIFICATION_TYPE_LABELS.warning}<Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>(Orange)</Box></MenuItem>
                  <MenuItem value="feature">{NOTIFICATION_TYPE_LABELS.feature}<Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>(Purple)</Box></MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Expires At"
                type="datetime-local"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Action Link (Optional)"
                placeholder="https://..."
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                InputProps={{
                  startAdornment: <LinkIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Link Button Text"
                placeholder="Learn More"
                value={formData.linkText}
                onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isModal}
                    onChange={(e) => setFormData({ ...formData, isModal: e.target.checked })}
                  />
                }
                label="Show as Modal (blocks UI until closed)"
              />
            </Grid>
            {selectedNotification && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as NotificationStatus })}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
          >
            {selectedNotification ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !formLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this notification? 
            This action cannot be undone.
          </Typography>
          {selectedNotification && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {selectedNotification.title || 'Untitled'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNotification.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
